import React, { useEffect, useRef, useState } from "react";
import { Avatar, Button } from "antd";
import {
  BoldOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  FileOutlined,
  FilePdfOutlined,
  ItalicOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  SendOutlined,
  SmileOutlined,
  StrikethroughOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { EmojiClickData } from "emoji-picker-react";
import { useChat } from "./ChatContext";
import { resolveAvatarUrl, uploadFile } from "./zulipClient";
import EmojiPickerPopover from "./EmojiPickerPopover";
import type { ZulipUser } from "./types";

type Props = {
  onSend: (content: string) => Promise<void> | void;
  placeholder?: string;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  /** How often to re-send "start" while typing continues, and how long idle before auto-"stop" — from Zulip's own `register` response (see useZulipEvents.ts). */
  typingIntervals?: { startMs: number; stopMs: number };
};

const MAX_MENTION_RESULTS = 6;
const MENTION_POPUP_WIDTH = 220;
const MENTION_ROW_HEIGHT = 34;
const FORMAT_TOOLBAR_SIZE = { width: 150, height: 36 };

// Zulip's own webapp media-embed rule (`web/src/upload.ts`): image and these
// audio types get `![]` (auto-embedded inline), everything else gets a plain
// `[]` link — video still auto-previews from a plain link.
const SUPPORTED_AUDIO_TYPES = new Set(["audio/aac", "audio/flac", "audio/mpeg", "audio/wav", "audio/x-wav", "audio/vnd.wave"]);

type FloatingPos = { left: number; top: number };
type MentionState = { start: number; query: string; matches: ZulipUser[]; activeIndex: number; pos: FloatingPos | null };
type AttachmentStatus = "uploading" | "done" | "error";
type StagedAttachment = {
  id: string;
  file: File;
  previewUrl: string | null;
  status: AttachmentStatus;
  url?: string;
  filename?: string;
  error?: string;
  abortController: AbortController;
};

// Finds an in-progress "@token" ending at `cursor`, Discord/Slack-style: the
// "@" must start a word (line start or preceded by whitespace) and nothing
// between it and the cursor may be whitespace yet — once a space is typed
// the mention token is considered "closed" (no longer being composed).
function findMentionTrigger(text: string, cursor: number): { start: number; query: string } | null {
  const before = text.slice(0, cursor);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;
  const precedingChar = at === 0 ? undefined : before[at - 1];
  if (precedingChar !== undefined && !/\s/.test(precedingChar)) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, query };
}

function matchUsers(users: ZulipUser[], query: string): ZulipUser[] {
  const q = query.toLowerCase();
  const starts: ZulipUser[] = [];
  const contains: ZulipUser[] = [];
  for (const u of users) {
    const name = u.full_name.toLowerCase();
    if (!q || name.startsWith(q)) starts.push(u);
    else if (name.includes(q)) contains.push(u);
  }
  return [...starts, ...contains].slice(0, MAX_MENTION_RESULTS);
}

function isMediaAttachment(mime: string): boolean {
  return mime.startsWith("image/") || SUPPORTED_AUDIO_TYPES.has(mime);
}

// Plain <textarea> elements have no API for reading the screen position of a
// caret/selection boundary. This is the standard "mirror div" workaround: an
// offscreen element replicating the textarea's text-affecting styles, with a
// marker <span> inserted at `position` whose layout offset tells us where
// that character sits. Used to anchor the floating format toolbar and the
// @-mention popup directly at the caret (Discord/Notion-style) instead of
// just above the whole composer.
function getCaretCoordinates(el: HTMLTextAreaElement, position: number): FloatingPos {
  const computed = window.getComputedStyle(el);
  const mirror = document.createElement("div");
  const style = mirror.style;
  style.position = "absolute";
  style.visibility = "hidden";
  style.whiteSpace = "pre-wrap";
  style.wordWrap = "break-word";
  style.boxSizing = computed.boxSizing;
  style.width = computed.width;
  style.borderTopWidth = computed.borderTopWidth;
  style.borderRightWidth = computed.borderRightWidth;
  style.borderBottomWidth = computed.borderBottomWidth;
  style.borderLeftWidth = computed.borderLeftWidth;
  style.borderTopStyle = computed.borderTopStyle;
  style.borderRightStyle = computed.borderRightStyle;
  style.borderBottomStyle = computed.borderBottomStyle;
  style.borderLeftStyle = computed.borderLeftStyle;
  style.paddingTop = computed.paddingTop;
  style.paddingRight = computed.paddingRight;
  style.paddingBottom = computed.paddingBottom;
  style.paddingLeft = computed.paddingLeft;
  style.fontStyle = computed.fontStyle;
  style.fontVariant = computed.fontVariant;
  style.fontWeight = computed.fontWeight;
  style.fontStretch = computed.fontStretch;
  style.fontSize = computed.fontSize;
  style.lineHeight = computed.lineHeight;
  style.fontFamily = computed.fontFamily;
  style.textAlign = computed.textAlign;
  style.textTransform = computed.textTransform;
  style.textIndent = computed.textIndent;
  style.letterSpacing = computed.letterSpacing;
  style.wordSpacing = computed.wordSpacing;

  document.body.appendChild(mirror);
  mirror.textContent = el.value.slice(0, position);
  const marker = document.createElement("span");
  marker.textContent = el.value.slice(position) || ".";
  mirror.appendChild(marker);

  const elRect = el.getBoundingClientRect();
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderLeft = parseFloat(computed.borderLeftWidth) || 0;
  const left = elRect.left + marker.offsetLeft + borderLeft - el.scrollLeft;
  const top = elRect.top + marker.offsetTop + borderTop - el.scrollTop;

  document.body.removeChild(mirror);
  return { left, top };
}

// Keeps a floating panel (format toolbar / mention popup) fully on-screen:
// clamps horizontally within the viewport margin, and flips below the
// anchor point instead of above it when there isn't room above.
function clampFloatingPosition(anchor: FloatingPos, width: number, height: number): FloatingPos {
  const margin = 8;
  const left = Math.min(Math.max(anchor.left, margin), Math.max(margin, window.innerWidth - width - margin));
  const above = anchor.top - height - 6;
  const top = above < margin ? anchor.top + 22 : above;
  return { left, top };
}

const Composer: React.FC<Props> = ({ onSend, placeholder, onTypingStart, onTypingStop, typingIntervals }) => {
  const { realmUsers, myUserId, pendingComposerText, consumePendingComposerText } = useChat();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [mention, setMention] = useState<MentionState | null>(null);
  const [toolbar, setToolbar] = useState<FloatingPos | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastStartSentAtRef = useRef(0);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attachmentsRef = useRef<StagedAttachment[]>([]);
  const dragCounterRef = useRef(0);

  const startMs = typingIntervals?.startMs ?? 10000;
  const stopMs = typingIntervals?.stopMs ?? 5000;

  // Auto-grow the textarea to fit its content (capped, then scrolls) — same
  // effect used by CallWidget's SMS composer.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // "Quote message" (see MessageBubble) stages its markdown block in
  // ChatContext rather than pushing it in some other way, since Composer is
  // the only place that owns the draft text — pick it up as soon as it
  // appears, appending after any draft already being typed, then hand the
  // context back to null so it isn't re-applied on an unrelated re-render.
  useEffect(() => {
    if (pendingComposerText === null) return;
    setText((prev) => (prev.trim() ? `${prev}\n${pendingComposerText}` : pendingComposerText));
    consumePendingComposerText();
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [pendingComposerText, consumePendingComposerText]);

  // Stop signaling "typing" (and cancel the pending auto-stop) if this
  // Composer instance goes away mid-compose — e.g. the thread was switched
  // (ThreadView keys Composer by selection, so this also covers that case).
  useEffect(() => () => {
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    if (lastStartSentAtRef.current) onTypingStop?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same thread-switch/unmount case, for any attachment still uploading or
  // staged: abort the request and free its object URL rather than leaking
  // either into the next conversation's Composer instance.
  useEffect(() => () => {
    attachmentsRef.current.forEach((a) => {
      a.abortController.abort();
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    });
  }, []);

  const handleChangeText = (value: string, cursor: number) => {
    setText(value);
    setToolbar(null);

    const trigger = findMentionTrigger(value, cursor);
    if (trigger) {
      const matches = matchUsers(realmUsers.filter((u) => u.user_id !== myUserId), trigger.query);
      if (matches.length) {
        const el = textareaRef.current;
        const coords = el ? getCaretCoordinates(el, trigger.start) : null;
        const rowCount = Math.min(matches.length, MAX_MENTION_RESULTS);
        const pos = coords ? clampFloatingPosition(coords, MENTION_POPUP_WIDTH, rowCount * MENTION_ROW_HEIGHT + 8) : null;
        setMention({ start: trigger.start, query: trigger.query, matches, activeIndex: 0, pos });
      } else {
        setMention(null);
      }
    } else {
      setMention(null);
    }

    if (!onTypingStart && !onTypingStop) return;

    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);

    if (!value.trim()) {
      lastStartSentAtRef.current = 0;
      onTypingStop?.();
      return;
    }

    const now = Date.now();
    if (now - lastStartSentAtRef.current > startMs) {
      onTypingStart?.();
      lastStartSentAtRef.current = now;
    }
    stopTimeoutRef.current = setTimeout(() => {
      lastStartSentAtRef.current = 0;
      onTypingStop?.();
    }, stopMs);
  };

  // Inserts Zulip's own mention syntax (`@**Full Name** `) in place of the
  // "@query" token being composed, then restores focus/caret right after it
  // — so typing can continue immediately, same as Discord/Slack.
  const applyMention = (user: ZulipUser) => {
    if (!mention) return;
    const before = text.slice(0, mention.start);
    const after = text.slice(mention.start + 1 + mention.query.length);
    const insertion = `@**${user.full_name}** `;
    setText(`${before}${insertion}${after}`);
    setMention(null);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      const pos = before.length + insertion.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  // Toggle-aware selection wrap/unwrap, mirroring Zulip's own webapp
  // formatting toolbar (`compose_ui.ts`'s `format_text`): wraps the current
  // selection in the given markdown syntax, or un-wraps it if the selection
  // is already immediately bounded by that syntax. With no selection, just
  // inserts the syntax pair with the caret left in between.
  const wrapSelection = (before: string, after: string = before) => {
    const el = textareaRef.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end, value } = el;
    const selected = value.slice(start, end);

    const isWrapped =
      start >= before.length &&
      value.length - end >= after.length &&
      value.slice(start - before.length, start) === before &&
      value.slice(end, end + after.length) === after;

    let next: string;
    let nextStart: number;
    let nextEnd: number;
    if (isWrapped) {
      next = value.slice(0, start - before.length) + selected + value.slice(end + after.length);
      nextStart = start - before.length;
      nextEnd = end - before.length;
    } else {
      next = value.slice(0, start) + before + selected + after + value.slice(end);
      nextStart = start + before.length;
      nextEnd = end + before.length;
    }

    setText(next);
    setToolbar(null);
    requestAnimationFrame(() => {
      const el2 = textareaRef.current;
      if (!el2) return;
      el2.focus();
      el2.setSelectionRange(nextStart, nextEnd);
    });
  };

  // Shows the floating format toolbar above the current selection (Discord's
  // actual behavior — no permanently-visible formatting row), hides it once
  // the selection collapses.
  const updateToolbar = () => {
    const el = textareaRef.current;
    if (!el || el.selectionStart === el.selectionEnd) {
      setToolbar(null);
      return;
    }
    const coords = getCaretCoordinates(el, el.selectionStart);
    setToolbar(clampFloatingPosition(coords, FORMAT_TOOLBAR_SIZE.width, FORMAT_TOOLBAR_SIZE.height));
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + emojiData.emoji + text.slice(end);
    setText(next);
    setEmojiOpen(false);
    const pos = start + emojiData.emoji.length;
    requestAnimationFrame(() => {
      const el2 = textareaRef.current;
      if (!el2) return;
      el2.focus();
      el2.setSelectionRange(pos, pos);
    });
  };

  const startUpload = (attachment: StagedAttachment) => {
    uploadFile(attachment.file, { signal: attachment.abortController.signal })
      .then(({ url, filename }) => {
        setAttachments((prev) => prev.map((a) => (a.id === attachment.id ? { ...a, status: "done", url, filename } : a)));
      })
      .catch((err: any) => {
        if (err?.name === "AbortError") return; // removed mid-upload — already gone from state
        setAttachments((prev) =>
          prev.map((a) => (a.id === attachment.id ? { ...a, status: "error", error: err?.message || "Upload failed" } : a)),
        );
      });
  };

  const stageFiles = (files: FileList | File[]) => {
    Array.from(files).forEach((file) => {
      const attachment: StagedAttachment = {
        id: crypto.randomUUID(),
        file,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
        status: "uploading",
        abortController: new AbortController(),
      };
      setAttachments((prev) => [...prev, attachment]);
      startUpload(attachment);
    });
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target) {
        target.abortController.abort();
        if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) stageFiles(e.target.files);
    e.target.value = "";
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragOver(false);
    if (e.dataTransfer.files?.length) stageFiles(e.dataTransfer.files);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].kind === "file") {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) {
      e.preventDefault();
      stageFiles(files);
    }
  };

  const canSend =
    (text.trim().length > 0 || attachments.some((a) => a.status === "done")) &&
    !attachments.some((a) => a.status === "uploading" || a.status === "error") &&
    !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setMention(null);
    setToolbar(null);
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    if (lastStartSentAtRef.current) {
      lastStartSentAtRef.current = 0;
      onTypingStop?.();
    }
    const attachmentMarkdown = attachments
      .filter((a) => a.status === "done" && a.url)
      .map((a) => {
        const name = a.filename ?? a.file.name;
        return isMediaAttachment(a.file.type) ? `![${name}](${a.url})` : `[${name}](${a.url})`;
      })
      .join("\n");
    const content = [text.trim(), attachmentMarkdown].filter(Boolean).join("\n");
    const sentAttachments = attachments;
    setSending(true);
    try {
      await onSend(content);
      setText("");
      sentAttachments.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      setAttachments([]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
      e.preventDefault();
      wrapSelection("**");
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "i") {
      e.preventDefault();
      wrapSelection("*");
      return;
    }
    if (mention) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMention((m) => (m ? { ...m, activeIndex: (m.activeIndex + 1) % m.matches.length } : m));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMention((m) => (m ? { ...m, activeIndex: (m.activeIndex - 1 + m.matches.length) % m.matches.length } : m));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        applyMention(mention.matches[mention.activeIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-composer">
      {toolbar && (
        <div className="chat-format-toolbar" style={{ left: toolbar.left, top: toolbar.top }}>
          <Button type="text" size="small" title="Bold" icon={<BoldOutlined />} onMouseDown={(e) => { e.preventDefault(); wrapSelection("**"); }} />
          <Button type="text" size="small" title="Italic" icon={<ItalicOutlined />} onMouseDown={(e) => { e.preventDefault(); wrapSelection("*"); }} />
          <Button type="text" size="small" title="Strikethrough" icon={<StrikethroughOutlined />} onMouseDown={(e) => { e.preventDefault(); wrapSelection("~~"); }} />
          <Button type="text" size="small" title="Code" icon={<CodeOutlined />} onMouseDown={(e) => { e.preventDefault(); wrapSelection("`"); }} />
        </div>
      )}

      {mention && (
        <div
          className={`chat-mention-popup${mention.pos ? " chat-mention-popup--floating" : ""}`}
          style={mention.pos ? { left: mention.pos.left, top: mention.pos.top } : undefined}
        >
          {mention.matches.map((u, i) => (
            <div
              key={u.user_id}
              className={`chat-mention-popup__row${i === mention.activeIndex ? " chat-mention-popup__row--active" : ""}`}
              // mousedown (not click) + preventDefault keeps focus on the
              // textarea so the blur handler below never fires for this.
              onMouseDown={(e) => {
                e.preventDefault();
                applyMention(u);
              }}
              onMouseEnter={() => setMention((m) => (m ? { ...m, activeIndex: i } : m))}
            >
              <Avatar size={22} src={resolveAvatarUrl(u.avatar_url) || undefined} icon={<UserOutlined />}>
                {!u.avatar_url && u.full_name.charAt(0).toUpperCase()}
              </Avatar>
              <span className="chat-mention-popup__name">{u.full_name}</span>
            </div>
          ))}
        </div>
      )}

      <div
        className={`chat-composer__box${dragOver ? " chat-composer__box--drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {attachments.length > 0 && (
          <div className="chat-composer__attachments">
            {attachments.map((a) => (
              <div key={a.id} className={`chat-composer__chip${a.status === "error" ? " chat-composer__chip--error" : ""}`}>
                {a.previewUrl ? (
                  <div className="chat-composer__chip-thumb" style={{ backgroundImage: `url(${a.previewUrl})` }} />
                ) : (
                  <span className="chat-composer__chip-icon">
                    {a.file.type === "application/pdf" ? <FilePdfOutlined /> : <FileOutlined />}
                  </span>
                )}
                <span className="chat-composer__chip-name">{a.file.name}</span>
                {a.status === "uploading" && <LoadingOutlined className="chat-composer__chip-spinner" />}
                <Button
                  type="text"
                  size="small"
                  shape="circle"
                  className="chat-composer__chip-remove"
                  icon={<CloseCircleOutlined />}
                  onClick={() => removeAttachment(a.id)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="chat-composer__row">
          <Button
            type="text"
            shape="circle"
            className="chat-composer__icon-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => fileInputRef.current?.click()}
          />
          <textarea
            ref={textareaRef}
            className="chat-composer__textarea"
            value={text}
            onChange={(e) => handleChangeText(e.target.value, e.target.selectionStart ?? e.target.value.length)}
            onKeyDown={handleKeyDown}
            onKeyUp={updateToolbar}
            onSelect={updateToolbar}
            onMouseUp={updateToolbar}
            onPaste={handlePaste}
            onBlur={() => {
              setMention(null);
              setToolbar(null);
            }}
            placeholder={placeholder ?? "Message…"}
            rows={1}
          />
          <div className="chat-composer__tray">
            <EmojiPickerPopover open={emojiOpen} onOpenChange={setEmojiOpen} placement="topRight" onEmojiClick={handleEmojiClick}>
              <Button type="text" shape="circle" className="chat-composer__icon-btn" icon={<SmileOutlined />} />
            </EmojiPickerPopover>
            <Button
              type="text"
              shape="circle"
              className={`chat-composer__send-btn${canSend ? " chat-composer__send-btn--active" : ""}`}
              icon={<SendOutlined />}
              loading={sending}
              disabled={!canSend}
              onClick={handleSend}
            />
          </div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesSelected} />
    </div>
  );
};

export default Composer;
